/**
 * The chart of accounts, in the office's hands.
 *
 * Laid out as a table that reads right to left like the office's old
 * system: the code in a column of its own (so every 111, 112, 113 sits
 * under the one before it), the name indented by level, the nature, and the
 * actions - shown when the row is pointed at, so a page of forty accounts is
 * not forty rows of buttons.
 *
 * Every account, the fixed ones included, is the office's to control: edit
 * its code, name, nature and on/off (a new code carries the accounts under
 * it along), move it up or down among its siblings, add accounts under it,
 * or delete it once nothing is under it.
 *
 * And it is real. Boxes and banks (under 111 / 113), expense and income
 * kinds (4 / 5), customers (61) and suppliers (621 / 622) are accounts in
 * it, each with its balance; a parent shows the sum of everything under
 * it, currency by currency. Adding under 111 makes a cash box - asked for
 * its currency and opening balance - under 4 an expense kind, under 61 a
 * customer on the customer form, and so on. Renaming or switching off a
 * box or a kind here does it to the box or the kind itself.
 *
 * Icons are drawn as SVG, not with the icon font: when the font is slow to
 * arrive the browser shows its ligature text instead ("ex", "fo", "lo").
 *
 * A pressed button turns into a spinner until the server has answered and
 * the chart is reloaded, and the other buttons wait meanwhile - a second
 * press never lands on a chart that is about to change under it. A move is
 * shown at once, and put back if the server refuses it.
 */

import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SearchIcon from "@mui/icons-material/Search";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import AddDialog from "components/AddDialog";
import VendorDialog from "layouts/vendors/VendorDialog";
import DialogShell, { FieldGrid } from "./DialogShell";
import { Field, Pick } from "./Fields";
import { api, problemOf, money, CURRENCIES } from "./api";

/** parent_id -> children, in the order the server sends them (the office's). */
export function childrenOf(accounts) {
  const out = {};
  accounts.forEach((a) => {
    const key = a.parent_id || "root";
    (out[key] = out[key] || []).push(a);
  });
  return out;
}

/**
 * The accounts a search keeps: every match, and every account above one,
 * so a match is always seen where it sits in the tree. null when there is
 * no search - everything shows.
 */
export function visibleFor(accounts, query) {
  const q = String(query || "")
    .trim()
    .toLowerCase();
  if (!q) return null;

  const byId = Object.fromEntries(accounts.map((a) => [a.id, a]));
  const keep = new Set();

  accounts.forEach((a) => {
    if (!`${a.code} ${a.name}`.toLowerCase().includes(q)) return;
    for (let at = a; at && !keep.has(at.id); at = byId[at.parent_id]) keep.add(at.id);
  });

  return keep;
}

/**
 * Every account's balance with everything under it added in, {id: {currency:
 * amount}}. A child read the other way from its parent - 62 دائن under 6
 * مدين - counts against it. Currencies are never added together.
 */
export function rolledUp(accounts) {
  const tree = childrenOf(accounts);
  const out = {};

  const walk = (a) => {
    const total = { ...(a.balance || {}) };
    (tree[a.id] || []).forEach((k) => {
      const sign = k.nature === a.nature ? 1 : -1;
      Object.entries(walk(k)).forEach(([cur, v]) => {
        total[cur] = Math.round(((total[cur] || 0) + sign * v) * 100) / 100;
      });
    });
    out[a.id] = total;
    return total;
  };

  (tree.root || []).forEach(walk);
  return out;
}

/** "76,000.00 CNY · 200.00 USD", the zeros left out - "" when there is nothing. */
const balanceLine = (totals) =>
  Object.entries(totals || {})
    .filter(([, v]) => Math.abs(v) >= 0.005)
    .map(([cur, v]) => `${money(v)} ${cur}`)
    .join(" · ");

/** Branches this big start folded - the customers and the suppliers. */
const FOLD_OVER = 15;

/**
 * How many accounts a branch shows at a time: drawing all 585 suppliers at
 * once held the page for seconds. The rest are a press away, and a search
 * finds any of them.
 */
const PAGE = 50;

/** What adding under each kind of branch makes. */
const ADDS = {
  box_cash: "صندوق جديد",
  box_bank: "بنك جديد",
  expense: "نوع مصروف جديد",
  income: "نوع إيراد جديد",
  customer: "زبون جديد",
  supplier: "مورد صيني جديد",
  vendor: "مورد جديد",
};

/** code | name | balance | nature | actions - the same on every row, so they line up. */
const GRID = "96px minmax(0, 1fr) 190px 72px 200px";
const INDENT = 30;

const NATURE = {
  debit: { label: "مدين", color: "#1565C0" },
  credit: { label: "دائن", color: "#2E7D32" },
};

const NATURES = [
  { id: "debit", name: "مدين · Debit" },
  { id: "credit", name: "دائن · Credit" },
];

/** One account's code, name, nature and on/off, edited together. */
/** What a plain account in each kind of branch can be made into. */
const BECOMES = {
  box_cash: "صندوق حقيقي",
  box_bank: "بنك حقيقي",
  expense: "نوع مصروف حقيقي",
  income: "نوع إيراد حقيقي",
};

/** A plain account where only a real one belongs - added before the chart was linked. */
export const unlinked = (account, parent, hasKids) =>
  Boolean(
    account &&
      parent &&
      !account.link_type &&
      !account.is_system &&
      !hasKids &&
      BECOMES[parent.zone]
  );

function AccountDialog({ account, parent, hasKids, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  // the currency and opening balance, when it is being made into a box
  const [becoming, setBecoming] = useState({ currency: "", opening_balance: "" });
  const [linking, setLinking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!account) return;
    setError("");
    setForm({
      code: account.code,
      name: account.name,
      nature: account.nature || "debit",
      is_active: Boolean(account.is_active),
    });
  }, [account]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const canBecome = unlinked(account, parent, hasKids);
  const asBox = canBecome && (parent.zone === "box_cash" || parent.zone === "box_bank");

  /** Make the plain account the box / bank / kind its place calls for. */
  const become = async () => {
    if (asBox && !becoming.currency) return setError("اختار عملة الصندوق");
    setLinking(true);
    setError("");
    try {
      await api.post(`/accounts/${account.id}/link`, {
        currency: becoming.currency || null,
        opening_balance: Number(becoming.opening_balance) || 0,
      });
      setLinking(false);
      onSaved();
      onClose();
    } catch (e) {
      setLinking(false);
      setError(problemOf(e, "Could not make it real"));
    }
    return null;
  };
  // a customer's or supplier's name - and a customer's or Chinese supplier's
  // on/off - belong to their own records, edited on their own pages
  const ownName = ["customer", "supplier", "vendor"].includes(account?.link_type);
  const ownStatus = ["customer", "supplier"].includes(account?.link_type);

  const save = async () => {
    if (!form.name.trim()) return setError("Give the account a name");
    if (!/^\d+$/.test(String(form.code).trim())) return setError("A code is digits only");
    if (parent && !String(form.code).trim().startsWith(parent.code))
      return setError(`The code has to start with ${parent.code}`);
    setSaving(true);
    setError("");
    try {
      await api.put(`/accounts/${account.id}`, { ...form, code: String(form.code).trim() });
      setSaving(false);
      onSaved();
      onClose();
    } catch (e) {
      setSaving(false);
      setError(problemOf(e, "Could not save the account"));
    }
    return null;
  };

  return (
    <DialogShell
      open={Boolean(account)}
      title={account ? `تعديل الحساب ${account.code}` : ""}
      onClose={onClose}
      onSave={save}
      saving={saving}
      error={error}
    >
      {form && (
        <SuiBox dir="rtl">
          <FieldGrid>
            <Field
              label="الرمز · Code"
              value={form.code}
              onChange={(e) => set({ code: e.target.value })}
              helperText={
                parent
                  ? `يبدأ بـ ${parent.code} - الحسابات تحته تتغير معه`
                  : "الحسابات تحته تتغير معه"
              }
              inputProps={{ inputMode: "numeric", "aria-label": "Account code" }}
            />
            <Pick
              label="الطبيعة · Nature"
              options={NATURES}
              value={form.nature}
              onChange={(id) => set({ nature: id || "debit" })}
              getLabel={(n) => n.name}
            />
          </FieldGrid>
          <Field
            label="اسم الحساب · Name"
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
            disabled={ownName}
            helperText={ownName ? "الاسم يتعدّل من صفحة الزبون أو المورد نفسه" : " "}
            inputProps={{ "aria-label": "Account name" }}
          />
          <FormControlLabel
            sx={{ mr: 0 }}
            control={
              <Switch
                checked={form.is_active}
                disabled={ownStatus}
                onChange={(e) => set({ is_active: e.target.checked })}
              />
            }
            label={form.is_active ? "فعّال" : "موقوف"}
          />

          {canBecome && (
            <SuiBox
              mt={1.5}
              p={1.5}
              sx={{ border: "1px dashed rgba(201,162,39,0.6)", borderRadius: "8px" }}
            >
              <SuiTypography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>
                هاد الحساب اسم بس - حوّله ل{BECOMES[parent.zone]} بنفس الاسم
              </SuiTypography>
              <SuiBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
                {asBox && (
                  <>
                    <TextField
                      select
                      size="small"
                      value={becoming.currency}
                      onChange={(e) => setBecoming({ ...becoming, currency: e.target.value })}
                      SelectProps={{ native: true }}
                      inputProps={{ "aria-label": "Currency to become" }}
                      sx={{ width: 120 }}
                    >
                      <option value="">العملة</option>
                      {CURRENCIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      type="number"
                      placeholder="الرصيد الافتتاحي"
                      value={becoming.opening_balance}
                      onChange={(e) =>
                        setBecoming({ ...becoming, opening_balance: e.target.value })
                      }
                      inputProps={{ step: "any", "aria-label": "Opening balance to become" }}
                      sx={{ width: 150 }}
                    />
                  </>
                )}
                <Button variant="contained" color="primary" onClick={become} disabled={linking}>
                  {linking ? "..." : `حوّله ل${BECOMES[parent.zone]}`}
                </Button>
              </SuiBox>
            </SuiBox>
          )}
        </SuiBox>
      )}
    </DialogShell>
  );
}

AccountDialog.defaultProps = { account: null, parent: null, hasKids: false };
AccountDialog.propTypes = {
  account: PropTypes.shape({
    id: PropTypes.number,
    code: PropTypes.string,
    name: PropTypes.string,
    nature: PropTypes.string,
    is_active: PropTypes.bool,
    link_type: PropTypes.string,
  }),
  parent: PropTypes.shape({ code: PropTypes.string, zone: PropTypes.string }),
  hasKids: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

function ChartTab() {
  const [accounts, setAccounts] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  // ids folded shut; everything starts open, as the tree is small
  const [folded, setFolded] = useState(() => new Set());
  // {parentId, name} while an account is being added
  const [adding, setAdding] = useState(null);
  // the account in the edit window
  const [editing, setEditing] = useState(null);
  // "customer" | "supplier" | "vendor" while that form is open
  const [making, setMaking] = useState(null);
  // "<id>:<what>" while a press is being carried out - "12:up", "new"
  const [busy, setBusy] = useState(null);
  // {branch id: how many of its accounts are shown}, past the first PAGE
  const [shown, setShown] = useState({});

  const load = () =>
    api
      .get("/accounts")
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : [];
        // the first time only: the hundreds of customers and suppliers
        // start folded, so the chart opens on its shape
        if (accounts === null) {
          const counts = {};
          list.forEach((a) => {
            if (a.parent_id) counts[a.parent_id] = (counts[a.parent_id] || 0) + 1;
          });
          setFolded(
            new Set(
              Object.keys(counts)
                .filter((id) => counts[id] > FOLD_OVER)
                .map(Number)
            )
          );
        }
        setAccounts(list);
        setError("");
      })
      .catch((e) => {
        setAccounts([]);
        setError(problemOf(e, "Could not load the chart of accounts"));
      });

  useEffect(() => {
    load();
  }, []);

  const tree = useMemo(() => childrenOf(accounts || []), [accounts]);
  const byId = useMemo(
    () => Object.fromEntries((accounts || []).map((a) => [a.id, a])),
    [accounts]
  );
  const visible = useMemo(() => visibleFor(accounts || [], query), [accounts, query]);
  const totals = useMemo(() => rolledUp(accounts || []), [accounts]);

  const run = async (key, fn) => {
    if (busy) return false;
    setBusy(key);
    setError("");
    try {
      await fn();
      await load();
      return true;
    } catch (e) {
      // whatever was shown ahead of the server goes back to how it is - and
      // the reason is said after, as a good reload clears the message
      await load();
      setError(problemOf(e, "Could not save"));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const toggle = (id) =>
    setFolded((f) => {
      const next = new Set(f);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const startAdding = (a) => {
    // customers and suppliers are made on their own forms, and land in the chart by themselves
    if (["customer", "supplier", "vendor"].includes(a.zone)) {
      setMaking(a.zone);
      return;
    }
    setAdding({ parentId: a.id, zone: a.zone, name: "", currency: "", opening_balance: "" });
    // open the parent so the new account shows where it lands
    setFolded((f) => {
      const next = new Set(f);
      next.delete(a.id);
      return next;
    });
  };

  const isBox = (zone) => zone === "box_cash" || zone === "box_bank";

  const add = async () => {
    if (!adding || !adding.name.trim()) return;
    if (isBox(adding.zone) && !adding.currency) {
      setError("اختار عملة الصندوق");
      return;
    }
    const body = { parent_id: adding.parentId, name: adding.name };
    if (isBox(adding.zone)) {
      body.currency = adding.currency;
      body.opening_balance = Number(adding.opening_balance) || 0;
    }
    if (await run("new", () => api.post("/accounts", body))) setAdding(null);
  };

  const move = (a, direction) => {
    if (busy) return;
    // shown at once: the account swaps places with its neighbour on screen
    const siblings = tree[a.parent_id || "root"] || [];
    const other = siblings[siblings.indexOf(a) + (direction === "up" ? -1 : 1)];
    if (other) {
      setAccounts((list) =>
        list.map((x) => {
          if (x.id === a.id) return other;
          if (x.id === other.id) return a;
          return x;
        })
      );
    }
    run(`${a.id}:${direction}`, () => api.post(`/accounts/${a.id}/move`, { direction }));
  };

  const remove = (a) => {
    // eslint-disable-next-line no-alert
    if (window.confirm(`حذف ${a.code} - ${a.name}؟`))
      run(`${a.id}:delete`, () => api.del(`/accounts/${a.id}`));
  };

  /** The button's icon, or a spinner while its press is carried out. */
  const pressed = (a, what, icon) =>
    busy === `${a.id}:${what}` ? <CircularProgress size={15} color="inherit" /> : icon;

  /** Nature is said on the main accounts, and wherever a child differs from its parent. */
  const showNature = (a) => {
    const parent = byId[a.parent_id];
    return !parent || parent.nature !== a.nature;
  };

  const renderNode = (a) => {
    if (visible && !visible.has(a.id)) return null;

    const kids = tree[a.id] || [];
    const siblings = tree[a.parent_id || "root"] || [];
    const at = siblings.indexOf(a);
    // a search opens every branch that holds a match
    const open = visible ? true : !folded.has(a.id);
    const top = a.level === 1;
    const nature = NATURE[a.nature] || NATURE.debit;
    let Kind = DescriptionOutlinedIcon;
    if (kids.length) Kind = open ? FolderOpenOutlinedIcon : FolderOutlinedIcon;

    return (
      <SuiBox key={a.id}>
        <SuiBox
          data-account={a.code}
          display="grid"
          alignItems="center"
          sx={({ palette, functions: { rgba } }) => ({
            gridTemplateColumns: GRID,
            minHeight: top ? 46 : 40,
            px: 1.5,
            backgroundColor: top ? rgba(palette.primary.main, 0.07) : "transparent",
            borderTop: top ? `1px solid ${rgba(palette.dark.main, 0.1)}` : "none",
            borderBottom: `1px solid ${rgba(palette.dark.main, 0.05)}`,
            opacity: a.is_active ? 1 : 0.45,
            "&:hover": { backgroundColor: rgba(palette.primary.main, top ? 0.11 : 0.04) },
            "& .row-actions": {
              // the row being worked on keeps its spinner in sight
              opacity: busy && busy.startsWith(`${a.id}:`) ? 1 : 0,
              transition: "opacity 120ms ease",
            },
            "&:hover .row-actions, &:focus-within .row-actions": { opacity: 1 },
          })}
        >
          {/* code - never indented, so the codes read down one column */}
          <SuiTypography
            component="span"
            sx={{
              fontSize: 13.5,
              fontWeight: top ? 800 : 600,
              fontVariantNumeric: "tabular-nums",
              letterSpacing: 0.3,
            }}
          >
            {a.code}
          </SuiTypography>

          {/* name, indented by level: the arrow, the kind of row, the name */}
          <SuiBox
            display="flex"
            alignItems="center"
            gap={0.75}
            sx={{ paddingInlineStart: `${(a.level - 1) * INDENT}px`, minWidth: 0 }}
          >
            {kids.length ? (
              <IconButton
                size="small"
                aria-label={open ? "Fold" : "Unfold"}
                onClick={() => toggle(a.id)}
                disabled={Boolean(visible)}
                sx={{ p: 0.25 }}
              >
                {open ? (
                  <ExpandMoreIcon sx={{ fontSize: 20 }} />
                ) : (
                  <ChevronLeftIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            ) : (
              <SuiBox width={24} flexShrink={0} />
            )}

            <Kind
              sx={{
                fontSize: 18,
                flexShrink: 0,
                color: kids.length ? "#C9A227" : "rgba(11,27,58,0.3)",
              }}
            />

            <SuiTypography
              component="span"
              noWrap
              sx={{ fontSize: top ? 15 : 14, fontWeight: kids.length ? 700 : 500 }}
            >
              {a.name}
            </SuiTypography>

            {kids.length > 0 && (
              <SuiTypography component="span" sx={{ fontSize: 12, opacity: 0.45, flexShrink: 0 }}>
                ({kids.length})
              </SuiTypography>
            )}
            {unlinked(a, byId[a.parent_id], kids.length > 0) && (
              <SuiBox
                component="span"
                title="اسم بس - افتح التعديل وحوّله لحقيقي"
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#B23A2E",
                  backgroundColor: "rgba(178,58,46,0.1)",
                  borderRadius: "6px",
                  px: 0.75,
                  flexShrink: 0,
                }}
              >
                غير مربوط
              </SuiBox>
            )}
            {a.link_label && (
              <SuiBox
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#8A6D12",
                  backgroundColor: "rgba(201,162,39,0.14)",
                  borderRadius: "6px",
                  px: 0.75,
                  flexShrink: 0,
                }}
              >
                {a.link_label}
              </SuiBox>
            )}
            {!a.is_active && (
              <SuiTypography component="span" sx={{ fontSize: 11, opacity: 0.8, flexShrink: 0 }}>
                · موقوف
              </SuiTypography>
            )}
          </SuiBox>

          {/* balance - its own, and everything under it */}
          <SuiTypography
            component="span"
            sx={{
              fontSize: 13,
              fontWeight: kids.length ? 700 : 500,
              fontVariantNumeric: "tabular-nums",
              direction: "ltr",
              textAlign: "right",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={balanceLine(totals[a.id])}
          >
            {balanceLine(totals[a.id]) || "-"}
          </SuiTypography>

          {/* nature */}
          <SuiBox>
            {showNature(a) && (
              <SuiBox
                component="span"
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: nature.color,
                  backgroundColor: `${nature.color}14`,
                  borderRadius: "6px",
                  px: 1,
                  py: 0.25,
                }}
              >
                {nature.label}
              </SuiBox>
            )}
          </SuiBox>

          {/* actions, shown on the row being pointed at */}
          <SuiBox
            className="row-actions"
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            gap={0.25}
          >
            <span title="لفوق">
              <IconButton
                size="small"
                aria-label="Move up"
                disabled={at <= 0 || Boolean(visible) || Boolean(busy)}
                onClick={() => move(a, "up")}
              >
                {pressed(a, "up", <ArrowUpwardIcon sx={{ fontSize: 17 }} />)}
              </IconButton>
            </span>
            <span title="لتحت">
              <IconButton
                size="small"
                aria-label="Move down"
                disabled={at === siblings.length - 1 || Boolean(visible) || Boolean(busy)}
                onClick={() => move(a, "down")}
              >
                {pressed(a, "down", <ArrowDownwardIcon sx={{ fontSize: 17 }} />)}
              </IconButton>
            </span>
            <span title="تعديل">
              <IconButton
                size="small"
                aria-label="Edit"
                disabled={Boolean(busy)}
                onClick={() => setEditing(a)}
              >
                <EditOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
            <span title={kids.length ? "احذف أو انقل الحسابات تحته أولاً" : "حذف"}>
              <IconButton
                size="small"
                aria-label="Delete"
                disabled={kids.length > 0 || Boolean(busy)}
                onClick={() => remove(a)}
                sx={{ color: "#D32F2F" }}
              >
                {pressed(a, "delete", <DeleteOutlineIcon sx={{ fontSize: 18 }} />)}
              </IconButton>
            </span>
            <span title={ADDS[a.zone] || "إضافة حساب فرعي"}>
              <IconButton
                size="small"
                aria-label="Add account under it"
                disabled={Boolean(busy)}
                onClick={() => startAdding(a)}
                sx={{ color: "#C9A227" }}
              >
                <AddIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </SuiBox>
        </SuiBox>

        {adding?.parentId === a.id && (
          <SuiBox
            display="flex"
            gap={1}
            py={1}
            sx={{ paddingInlineStart: `${96 + a.level * INDENT + 48}px`, px: 1.5 }}
          >
            <TextField
              size="small"
              autoFocus
              fullWidth
              placeholder={`${ADDS[adding.zone] || "حساب جديد"} تحت ${a.code} - ${a.name}`}
              value={adding.name}
              onChange={(e) => setAdding({ ...adding, name: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !busy) add();
                if (e.key === "Escape") setAdding(null);
              }}
            />
            {isBox(adding.zone) && (
              <>
                <TextField
                  select
                  size="small"
                  value={adding.currency}
                  onChange={(e) => setAdding({ ...adding, currency: e.target.value })}
                  SelectProps={{ native: true }}
                  inputProps={{ "aria-label": "Box currency" }}
                  sx={{ width: 120, flexShrink: 0 }}
                >
                  <option value="">العملة</option>
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  type="number"
                  placeholder="الرصيد الافتتاحي"
                  value={adding.opening_balance}
                  onChange={(e) => setAdding({ ...adding, opening_balance: e.target.value })}
                  inputProps={{ "aria-label": "Opening balance", step: "any" }}
                  sx={{ width: 150, flexShrink: 0 }}
                />
              </>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={add}
              disabled={Boolean(busy)}
              startIcon={busy === "new" ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {busy === "new" ? "جاري الإضافة..." : "إضافة"}
            </Button>
            <Button variant="outlined" color="dark" onClick={() => setAdding(null)}>
              إلغاء
            </Button>
          </SuiBox>
        )}

        {/* eslint-disable-next-line no-use-before-define -- the two call each other */}
        {open && renderKids(a, kids)}
      </SuiBox>
    );
  };

  /** A branch's accounts, PAGE at a time, and a row to show more. */
  function renderKids(a, kids) {
    const list = visible ? kids.filter((k) => visible.has(k.id)) : kids;
    const limit = shown[a.id] || PAGE;
    const more = list.length - limit;

    return (
      <>
        {list.slice(0, limit).map(renderNode)}
        {more > 0 && (
          <SuiBox
            display="flex"
            alignItems="center"
            gap={1}
            py={0.75}
            sx={({ palette, functions: { rgba } }) => ({
              paddingInlineStart: `${96 + a.level * INDENT + 48}px`,
              borderBottom: `1px solid ${rgba(palette.dark.main, 0.05)}`,
            })}
          >
            <SuiTypography component="span" sx={{ fontSize: 13, opacity: 0.6 }}>
              {`ظاهر ${limit} من ${list.length}`}
            </SuiTypography>
            <Button
              size="small"
              variant="text"
              color="dark"
              onClick={() => setShown((m) => ({ ...m, [a.id]: limit + PAGE }))}
            >
              {`عرض ${Math.min(PAGE, more)} كمان`}
            </Button>
            <Button
              size="small"
              variant="text"
              color="dark"
              onClick={() => setShown((m) => ({ ...m, [a.id]: list.length }))}
            >
              {`عرض الكل (${list.length})`}
            </Button>
          </SuiBox>
        )}
      </>
    );
  }

  const roots = tree.root || [];
  const nothingFound = visible && visible.size === 0;

  return (
    <SuiBox px={3} py={2} dir="rtl">
      <SuiBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1.5}
        mb={2}
      >
        <SuiBox>
          <SuiTypography variant="h6">شجرة الحسابات · Chart of Accounts</SuiTypography>
          <SuiTypography component="div" sx={{ fontSize: 12.5, opacity: 0.6 }}>
            الصناديق والبنوك وأنواع المصاريف والإيرادات والزبائن والموردين كلها حسابات هون، برصيدها.
            مرّر على أي حساب لتعديله أو ترتيبه أو إضافة تحته
          </SuiTypography>
        </SuiBox>

        <SuiBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <TextField
            size="small"
            placeholder="ابحث بالرمز أو الاسم..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ width: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, opacity: 0.5 }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            color="dark"
            size="small"
            disabled={Boolean(visible)}
            onClick={() => setFolded(new Set())}
          >
            فتح الكل
          </Button>
          <Button
            variant="outlined"
            color="dark"
            size="small"
            disabled={Boolean(visible)}
            onClick={() =>
              setFolded(new Set((accounts || []).filter((a) => tree[a.id]).map((a) => a.id)))
            }
          >
            طي الكل
          </Button>
        </SuiBox>
      </SuiBox>

      {error && (
        <SuiTypography variant="button" color="error" display="block" mb={1.5}>
          {error}
        </SuiTypography>
      )}

      {accounts === null ? (
        <CargoLoader label="Loading accounts" />
      ) : (
        <SuiBox
          sx={({ palette, borders, functions: { rgba } }) => ({
            border: `1px solid ${rgba(palette.dark.main, 0.1)}`,
            borderRadius: borders.borderRadius.lg,
            overflow: "hidden",
          })}
        >
          {/* column heads */}
          <SuiBox
            display="grid"
            alignItems="center"
            sx={({ palette, functions: { rgba } }) => ({
              gridTemplateColumns: GRID,
              px: 1.5,
              py: 1,
              backgroundColor: rgba(palette.dark.main, 0.04),
              borderBottom: `1px solid ${rgba(palette.dark.main, 0.1)}`,
            })}
          >
            {/* sized on each one - the typography's own size wins over the row's */}
            {["الرمز", "اسم الحساب", "الرصيد", "الطبيعة"].map((head) => (
              <SuiTypography
                key={head}
                component="span"
                sx={{ fontSize: 12, fontWeight: 700, opacity: 0.6, whiteSpace: "nowrap" }}
              >
                {head}
              </SuiTypography>
            ))}
            <span />
          </SuiBox>

          {roots.map(renderNode)}

          {nothingFound && (
            <SuiTypography sx={{ fontSize: 14, opacity: 0.6, p: 3, textAlign: "center" }}>
              لا يوجد حساب بهذا الرمز أو الاسم
            </SuiTypography>
          )}
        </SuiBox>
      )}

      {/* customers and suppliers are made on their own forms; the chart picks them up */}
      <AddDialog
        open={making === "customer"}
        entity="user"
        defaults={{ role_id: "customer" }}
        onClose={() => setMaking(null)}
        onCreated={load}
      />
      <AddDialog
        open={making === "supplier"}
        entity="supplier"
        onClose={() => setMaking(null)}
        onCreated={load}
      />
      <VendorDialog open={making === "vendor"} onClose={() => setMaking(null)} onSaved={load} />

      <AccountDialog
        account={editing}
        parent={editing ? byId[editing.parent_id] || null : null}
        hasKids={Boolean(editing && tree[editing.id])}
        onClose={() => setEditing(null)}
        onSaved={load}
      />
    </SuiBox>
  );
}

export default ChartTab;
